import { registrationConsent, saveRegistrationConsent } from '../../legal/registration';
import { getLocalStorage } from '../../utils/func';
import config from '../../utils/config';
import { legalCopy } from '../../legal/copy';
import usePageLayout from '../../hooks/usePageLayout';
import React, { useEffect, useRef, useState } from 'react';
import { Layout, Space, Row, Button, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import AuthButtonGoogleCustom from '../../components/AuthButtonGoogleCustom';
import AuthButtonApple from '../../components/AuthButtonApple';
import SocialAuthButtons from '../../socialAuth';
import ReviewAccess from '../../reviewAccess';
import WiwHeaderTitle from '../../components/WiwHeaderTitle';
import { HomeIcon, SquareIcon, CheckSquareIcon } from '../../components/Brand/Icon';
import { APP_ROUTES } from '../../utils/constant';
import { Helmet } from 'react-helmet';
import url from '../../utils/url';
import './index.scss';
import { deletionCopy } from '../../accountDeletion/copy';
import { deletedLoginNoticeKey, loginPurpose } from '../../accountDeletion/loginPurpose';
import { appSessionStorage } from '../../utils/storage';

const { Header, Content } = Layout;

export default function Login({ confirmSession = false, onConsentSaved, onSkip }) {
  const headerRef = useRef(null);
  const { contentHeight, contentTop } = usePageLayout(headerRef);
  const { t, i18n } = useTranslation();
  const { t: brand } = useTranslation('brand');
  const copy = legalCopy(i18n.language);
  const [consent, setConsent] = useState(false);
  const [deletedLogin, setDeletedLogin] = useState(false);
  const statusOnly = loginPurpose() === 'deletion_status';
  const accountCopy = deletionCopy(i18n.language);
  useEffect(() => {
    const show = () => { setDeletedLogin(true); try { appSessionStorage.removeItem(deletedLoginNoticeKey); } catch { /* notice stays visible */ } };
    window.addEventListener('wiw:account-login-deleted', show);
    try { if (appSessionStorage.getItem(deletedLoginNoticeKey)) show(); } catch { /* events remain available */ }
    return () => window.removeEventListener('wiw:account-login-deleted', show);
  }, []);
  const [activeProvider, setActiveProvider] = useState(null);
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const saving = useRef(false), controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  const confirm = async () => {
    if (!consent || saving.current) return;
    saving.current = true; setActiveProvider('session'); setError(false);
    const abort = new AbortController(); controller.current = abort;
    const token = getLocalStorage(config.token);
    try {
      await saveRegistrationConsent(token, registrationConsent(), { signal: abort.signal });
      if (!abort.signal.aborted && token === getLocalStorage(config.token)) onConsentSaved?.();
    } catch { if (!abort.signal.aborted) setError(true); }
    finally { if (!abort.signal.aborted) { saving.current = false; setActiveProvider(null); } }
  };

  return (
    <Layout className="app-layout login_page">
      <Helmet>
        <title>{t('login.title')}</title>
        <meta name="description" content={t('login.title')} />
      </Helmet>
      <Header className="header" ref={headerRef}><WiwHeaderTitle title={statusOnly ? accountCopy.check : t('login.title')}></WiwHeaderTitle></Header>
      <Content className='content-layot content-layout' style={{ height: contentHeight, top: contentTop }}>
        <Row justify="center" align="middle" style={{ width: '100%' }}>
          <Col span={24}>
            <Space direction="vertical" className='login_buttom' size={8} style={{ width: '100%' }}>
              {confirmSession ? <Button className="button_border" disabled={!consent || Boolean(activeProvider)} onClick={confirm}>
                {copy.save}
              </Button> : <>
              <AuthButtonGoogleCustom disabled={(!consent && !statusOnly) || Boolean(activeProvider)} onBusy={setActiveProvider} />
              <AuthButtonApple disabled={(!consent && !statusOnly) || Boolean(activeProvider)} onBusy={setActiveProvider} />
              <SocialAuthButtons disabled={(!consent && !statusOnly) || Boolean(activeProvider)} onBusy={setActiveProvider} />
              </>}
              {error && <p role="alert" className="error-message">{copy.error}</p>}
              <Button
                style={{ width: '100%' }}
                className='button_border'
                onClick={() => onSkip ? onSkip() : navigate(url('/'))}
                icon={<HomeIcon />}
              >
                {t('login.button_skip')}
              </Button>
              {!statusOnly && <><label className="privacy_wrap" htmlFor="login-consent">
                {t('login.consent')} <Link to={url(`${APP_ROUTES.STATIC}/terms`)}>{t('login.terms')}</Link> {t('login.and')} <Link to={url(`${APP_ROUTES.STATIC}/privacy`)}>{t('login.privacy')}</Link>
                <span id="login-consent-summary" className="login-consent-summary">{copy.registrationSummary}</span>
              </label>
              <label className="login-consent-control">
                <input id="login-consent" type="checkbox" checked={consent} disabled={Boolean(activeProvider)}
                  aria-describedby="login-consent-summary" aria-label={brand('authConsentLabel')} onChange={(event) => setConsent(event.target.checked)} />
                {consent ? <CheckSquareIcon size={32} filled /> : <SquareIcon size={32} />}
              </label>
              <details className="login-consent-details">
                <summary>{copy.registrationDetails}</summary>
                <p>{copy.registration}</p>
              </details>
              </>}
              {!confirmSession && !statusOnly && <ReviewAccess consent={consent} disabled={Boolean(activeProvider)} onBusy={setActiveProvider} />}
              {!confirmSession && !statusOnly && <Link to={url('/login') + '?deletion_status=1'}>{accountCopy.title}: {accountCopy.check}</Link>}
              {confirmSession && <Link to={url(APP_ROUTES.USER_SETTINGS)} state={{ settingsSection: 'account' }}>{accountCopy.title}</Link>}
              {deletedLogin && !statusOnly && <Link to={url('/login') + '?register_new=1'}>{accountCopy.newAccount}</Link>}
            </Space>
          </Col>
        </Row>
      </Content>
    </Layout>
  )
}
